import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import OwnerLogin from './pages/OwnerLogin';
import OwnerRegister from './pages/OwnerRegister';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Matches from './pages/Matches';
import NewMatch from './pages/NewMatch';
import MatchScoring from './pages/MatchScoring';
import Players from './pages/Players';
import PlayerProfile from './pages/PlayerProfile';
import Clubs from './pages/Clubs';
import Tournaments from './pages/Tournaments';
import Analytics from './pages/Analytics';
import TournamentDetail from './pages/TournamentDetail';
import LiveTournament from './pages/LiveTournament';
import Teams from './pages/Teams';
import Support from './pages/Support';
import MyProfile from './pages/MyProfile';
import Browse from './pages/Browse';
import ClubLeaderboard from './pages/ClubLeaderboard';
import Rankings from './pages/Rankings';
import Venues from './pages/Venues';
import Admin from './pages/Admin';
import ClubMembership from './pages/ClubMembership';
import Notifications from './pages/Notifications';
import Revenue from './pages/Revenue';
import MyDues from './pages/MyDues';
import GuestMatch from './pages/GuestMatch';
import QuickPlay from './pages/QuickPlay';
import MyStreaks from './pages/MyStreaks';
import Rivals from './pages/Rivals';
import LocalTournament from './pages/LocalTournament';
import SecretAdmin from './pages/SecretAdmin';
import HomeLanding from './pages/HomeLanding';
import WatchMatch from './pages/WatchMatch';
import BracketDemo from './pages/BracketDemo';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment (not query params) for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public — Player-First (zero-friction) */}
      <Route path="/" element={<HomeLanding />} />
      <Route path="/play" element={<QuickPlay />} />
      <Route path="/streaks" element={<MyStreaks />} />
      <Route path="/rivals" element={<Rivals />} />
      <Route path="/tournament" element={<LocalTournament />} />
      <Route path="/dev/bracket-demo" element={<BracketDemo />} />
      <Route path="/watch/:shortId" element={<WatchMatch />} />

      {/* Legacy public + share links */}
      <Route path="/landing" element={<Landing />} />
      <Route path="/guest" element={<GuestMatch />} />
      <Route path="/guest/:matchId" element={<GuestMatch />} />
      <Route path="/matches/:matchId" element={<MatchScoring />} />
      <Route path="/matches/:matchId/score" element={<MatchScoring />} />
      <Route path="/players/:userId" element={<PlayerProfile />} />
      <Route path="/tournaments/:tournamentId" element={<TournamentDetail />} />
      <Route path="/tournaments" element={<Tournaments />} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/live/tournament/:tournamentId" element={<LiveTournament />} />

      {/* Owner Auth */}
      <Route path="/owner-login" element={<OwnerLogin />} />
      <Route path="/owner-register" element={<OwnerRegister />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Secret admin (hidden) */}
      <Route path="/internal/dashboard" element={<SecretAdmin />} />

      {/* Owner-Only Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
      <Route path="/matches/new" element={<ProtectedRoute><NewMatch /></ProtectedRoute>} />
      <Route path="/players" element={<ProtectedRoute><Players /></ProtectedRoute>} />
      <Route path="/clubs" element={<ProtectedRoute><Clubs /></ProtectedRoute>} />
      <Route path="/clubs/:clubId" element={<ProtectedRoute><Clubs /></ProtectedRoute>} />
      <Route path="/clubs/:clubId/membership" element={<ProtectedRoute><ClubMembership /></ProtectedRoute>} />
      <Route path="/clubs/:clubId/leaderboard" element={<ProtectedRoute><ClubLeaderboard /></ProtectedRoute>} />
      <Route path="/browse" element={<ProtectedRoute><Browse /></ProtectedRoute>} />
      <Route path="/rankings" element={<ProtectedRoute><Rankings /></ProtectedRoute>} />
      <Route path="/venues" element={<ProtectedRoute><Venues /></ProtectedRoute>} />
      <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/revenue" element={<ProtectedRoute><Revenue /></ProtectedRoute>} />
      <Route path="/my-dues" element={<ProtectedRoute><MyDues /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRouter />
              <PWAInstallPrompt />
              <Toaster richColors position="top-right" />
            </AuthProvider>
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
